import { defineStore } from "pinia";
import { getLocaleBasedOfPreferredLanguages } from "shared-core/services/i18n.ts";
import {
  AUTH_TOKEN_META_DATA,
  AUTH_TOKEN_PRESENT,
  type AuthTokenMetaData,
} from "shared-core/types/auth.ts";
import {
  type ClientOrganization,
  type IdentityProvider,
  SignupSource,
} from "shared-core/types/organization.ts";
import type { RiskAnalysis } from "shared-core/types/risk-analysis.ts";
import { Role, type UserDataResponse } from "shared-core/types/user";
import { averageBreachCost } from "shared-core/utils/common";
import { verifyUserInCrisp } from "shared-ui/composables/use-crisp.ts";
import { usePostHog } from "shared-ui/composables/use-posthog.ts";
import { resetAdminState } from "shared-ui/stores/admin-state";
import type {
  AuthStore,
  AuthStoreActions,
  AuthStoreState,
} from "shared-ui/types/auth-store.ts";
import { assertSetupStoreCompatibility } from "shared-ui/types/store-type-assert.ts";
import { useApi } from "shared-ui/utils/api";
import { computed, ref, watch } from "vue";
import { type LocationQuery } from "vue-router";
import { router } from "@/router";
import { readCookie } from "@/utils/cookie.ts";

interface OrganizationAuthStore extends ClientOrganization {
  id: number;
  /** Alias kept for existing call sites; undefined for members. */
  riskAnalysis: RiskAnalysis | null | undefined;
}

const lastLoggedInProviderLocalStorageKey = "controla_last_logged_in_provider";

const assertAuthStoreCompatibility = assertSetupStoreCompatibility<
  AuthStore,
  AuthStoreActions
>();

export const useAuthStore = defineStore(
  "auth",
  () => {
    const user = ref<AuthStoreState["user"]>(null);
    const registrationCredential = ref<string | null>(null);
    const oauthParameters = ref<LocationQuery | null>(null);
    const organization = ref<OrganizationAuthStore | null>(null);
    // Session storage restores the user before checkAuth runs, so PostHog sync stays off until the session is known.
    const isImpersonating = ref(true);

    const isAuthenticated = computed(() => !!user.value);
    const isAdministrator = computed(() => user.value?.role === Role.Admin);
    const isControlaUser = computed(
      () => user.value?.email.endsWith("controla.ai") ?? false,
    );
    const hasRegistered = computed(
      () => user.value?.organizationStatus !== "PENDING",
    );
    const totalBlockedAttacks = computed(() => {
      const metrics = organization.value?.metrics?.stopped_attacks;
      return (metrics?.failed_logins ?? 0) + (metrics?.blocked_spam ?? 0);
    });
    const estimatedBreachCost = computed(() => {
      const cost =
        organization.value?.risk_analysis?.expectedBreachCostRange?.upper;
      return cost && cost > 0 ? cost : averageBreachCost;
    });
    const isTryggHansa = computed(
      () => user.value?.signupSource === SignupSource.TRYGG_HANSA,
    );
    const isGjensidige = computed(
      () => user.value?.signupSource === SignupSource.GJENSIDIGE,
    );
    const isIf = computed(() => user.value?.signupSource === SignupSource.IF);
    const isSvedea = computed(
      () => user.value?.signupSource === SignupSource.SVEDEA,
    );
    const getOauthParameters = computed(() => oauthParameters.value);

    function storeOauthParameters(newOauthParameters: LocationQuery): void {
      oauthParameters.value = newOauthParameters;
    }

    function resetOauthParameters(): void {
      oauthParameters.value = null;
    }

    async function logout(): Promise<void> {
      const api = useApi();
      try {
        const logoutResponse = await api.post("auth/logout");
        if (logoutResponse.responseStatus === 204) {
          await checkAuth(true);
          await fetchOrganization(true);
          await router.push("/impersonate/users");

          return;
        }
      } catch (error) {
        console.error(
          "Failed to log out in backend, will proceed in frontend",
          error,
        );
      }
      await clearSession();
      await router.push("/?logout=true");
    }

    async function clearSession(): Promise<void> {
      user.value = null;
      organization.value = null;
      oauthParameters.value = null;
      resetAdminState();
      (await usePostHog("on_reject")).posthog.reset();
    }

    async function checkAuth(
      force: boolean,
      options: { propagateError?: boolean } = {},
    ): Promise<void> {
      let hasAuthCookie;
      try {
        hasAuthCookie = new RegExp(`(?:^|;\\s*)${AUTH_TOKEN_PRESENT}=`).test(
          document.cookie,
        );
      } catch {
        hasAuthCookie = true;
      }

      const metadata = getMetadata();

      if (hasAuthCookie === false && !force) {
        return;
      }

      if (user.value && !force) {
        isImpersonating.value = !!metadata?.actorId;
        return;
      }

      const api = useApi();
      try {
        const response = await api.get<UserDataResponse>("auth/user-data");

        user.value = (await response.data).userData;
        // Update these together so identity watchers never observe the previous
        // user after an impersonation session has ended.
        isImpersonating.value = !!metadata?.actorId;
        if (!isImpersonating.value) {
          verifyUserInCrisp(user.value);
        }

        if (!organization.value) {
          organization.value = {} as OrganizationAuthStore;
        }
        organization.value.domain = user.value.companyDomain;

        localStorage.setItem(
          lastLoggedInProviderLocalStorageKey,
          user.value.lastUsedProvider,
        );
      } catch (error) {
        // Most auth probes rely on the API client redirecting after failures.
        // Registration completion must know whether the refresh succeeded.
        if (options.propagateError) throw error;
      }
    }

    async function syncPostHogIdentity(force = false): Promise<void> {
      if (!user.value || isImpersonating.value) {
        return;
      }

      const distinctId = String(user.value.email);
      const { posthog } = await usePostHog("on_reject");
      const companyGroupKey = user.value.organizationId.toString();
      const identityChanged = posthog.get_distinct_id() !== distinctId;
      const groupChanged = posthog.getGroups().company !== companyGroupKey;

      if (force || identityChanged) {
        posthog.identify(distinctId, {
          role: user.value.role,
          name: `${user.value.firstName} ${user.value.lastName}`,
          email: user.value.email,
          first_name: user.value.firstName,
          userId: user.value.id,
          preferred_language: getLocaleBasedOfPreferredLanguages(
            navigator.language,
            navigator.languages,
          ),
          organization_id: user.value.organizationId,
        });
      }

      if (force || groupChanged) {
        if (force && !groupChanged) {
          posthog.resetGroups();
        }
        posthog.group("company", companyGroupKey);
      }
    }

    async function fetchOrganization(force: boolean): Promise<void> {
      // `organization` is persisted in sessionStorage, so a payload stored
      // before productivity_suite existed has to be fetched again rather than
      // trusted.
      const isCached =
        organization.value?.id != null &&
        "productivity_suite" in organization.value;

      if (isCached && !force) {
        return;
      }

      if (!user.value) {
        return;
      }

      const api = useApi();
      // Members get the same endpoint with the admin-only fields left out.
      const response = await api.get<ClientOrganization>("organization");
      const responseData = await response.data;

      organization.value = {
        ...responseData,
        riskAnalysis: responseData.risk_analysis,
      };
    }

    function hasLoggedInBefore(): boolean {
      return localStorage.getItem(lastLoggedInProviderLocalStorageKey) !== null;
    }

    function getLastUsedProvider(): IdentityProvider | null {
      return localStorage.getItem(
        lastLoggedInProviderLocalStorageKey,
      ) as IdentityProvider | null;
    }

    watch([user, organization, isImpersonating], () => {
      void syncPostHogIdentity().catch(() => undefined);
    });

    return assertAuthStoreCompatibility({
      user,
      registrationCredential,
      oauthParameters,
      organization,
      isAuthenticated,
      isAdministrator,
      isImpersonating,
      isControlaUser,
      hasRegistered,
      totalBlockedAttacks,
      estimatedBreachCost,
      isTryggHansa,
      isGjensidige,
      isIf,
      isSvedea,
      getOauthParameters,
      storeOauthParameters,
      resetOauthParameters,
      logout,
      clearSession,
      checkAuth,
      syncPostHogIdentity,
      fetchOrganization,
      hasLoggedInBefore,
      getLastUsedProvider,
    });
  },
  {
    persist: {
      storage: sessionStorage,
      pick: ["user", "oauthParameters", "organization"],
    },
  },
);

function getMetadata(): AuthTokenMetaData | null {
  const cookieValue = readCookie(AUTH_TOKEN_META_DATA);
  if (!cookieValue) {
    return null;
  }

  try {
    return JSON.parse(cookieValue) as AuthTokenMetaData;
  } catch {
    // Ignore JSON parse errors
  }
  return null;
}
